import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import { involveOptions, heroImages } from "../mock";

const GetInvolved = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", interest: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    toast({ title: "Thank you for stepping up! 🙌", description: "Our volunteer coordinator will be in touch within a few days." });
    setForm({ name: "", email: "", interest: "", message: "" });
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <section className="pt-32 lg:pt-40 pb-16 bg-secondary/40 relative overflow-hidden">
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="container-prc relative text-center max-w-3xl mx-auto">
          <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-5">Get Involved</Badge>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-foreground text-balance">There’s a place for you here</h1>
          <p className="mt-5 text-lg text-muted-foreground">Give your time, talent, or support—every contribution helps a family thrive.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container-prc grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {involveOptions.map((o, i) => {
            const Icon = o.icon;
            return (
              <Reveal key={o.title} delay={i * 80}>
                <div className="card-hover bg-white rounded-2xl p-7 shadow-soft h-full">
                  <span className="grid place-items-center h-14 w-14 rounded-2xl bg-primary/10 text-primary mb-5"><Icon className="h-7 w-7" /></span>
                  <h3 className="font-display font-bold text-lg text-foreground">{o.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{o.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-20 bg-secondary/40">
        <div className="container-prc grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="img-zoom rounded-[2rem] overflow-hidden shadow-lift order-2 lg:order-1">
            <img src={heroImages.volunteer} alt="Volunteers" className="w-full h-[520px] object-cover" />
          </Reveal>
          <Reveal delay={100} className="order-1 lg:order-2">
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-foreground">Join our volunteer family</h2>
            <p className="mt-3 text-muted-foreground">Tell us a little about you and how you’d love to help. No experience needed—just heart.</p>
            <form onSubmit={submit} className="mt-7 space-y-4 bg-white rounded-2xl p-7 shadow-soft">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Name</Label>
                  <Input required value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-xl" placeholder="Jane Doe" />
                </div>
                <div>
                  <Label className="font-semibold">Email</Label>
                  <Input required type="email" value={form.email} onChange={set("email")} className="mt-1.5 h-11 rounded-xl" placeholder="jane@email.com" />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Area of interest</Label>
                <Input value={form.interest} onChange={set("interest")} className="mt-1.5 h-11 rounded-xl" placeholder="Classroom help, events, admin..." />
              </div>
              <div>
                <Label className="font-semibold">Message</Label>
                <Textarea value={form.message} onChange={set("message")} className="mt-1.5 rounded-xl min-h-[110px]" placeholder="Tell us about your availability and interests" />
              </div>
              <Button type="submit" className="btn-cta w-full bg-primary hover:bg-primary/90 rounded-full font-bold h-12">Submit Application <ArrowRight className="h-5 w-5 ml-2" /></Button>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="container-prc">
          <Reveal className="bg-primary rounded-[2rem] p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-8 text-primary-foreground">
            <div className="max-w-xl">
              <h2 className="font-display font-extrabold text-3xl">Prefer to give?</h2>
              <p className="mt-3 text-primary-foreground/85">A gift of any size keeps our programs free and accessible for families who need them most.</p>
              <ul className="mt-5 space-y-2">
                {["100% goes to programs", "Tax-deductible", "Monthly giving available"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-primary-foreground/90"><CheckCircle2 className="h-5 w-5 text-accent" /> {t}</li>
                ))}
              </ul>
            </div>
            <Button asChild className="btn-cta bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full px-8 h-12 text-base"><Link to="/donate"><Heart className="h-5 w-5 mr-2" /> Donate Now</Link></Button>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;
